/**
 * Presence Service
 *
 * Manages user online/offline status, last seen timestamps, and typing indicators.
 *
 * @module @municipality/chat/presence
 */

import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  type Unsubscribe,
  type DocumentSnapshot,
} from 'firebase/firestore';
import { db } from '@municipality/firebase';
import type { UserPresence, UserStatus, ChatScope } from '@municipality/types';

export class PresenceService {
  async updateStatus(
    userId: string,
    status: UserStatus,
    scope: ChatScope,
    municipalityId?: string
  ): Promise<void> {
    try {
      const presenceRef = doc(db, 'user_presence', userId);
      await setDoc(
        presenceRef,
        {
          userId,
          status,
          lastSeen: serverTimestamp(),
          scope,
          municipalityId: municipalityId ?? null,
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Failed to update user status:', error);
      throw new Error('Failed to update presence status');
    }
  }

  async getStatus(userId: string): Promise<UserPresence | null> {
    try {
      const presenceRef = doc(db, 'user_presence', userId);
      const presenceDoc = await getDoc(presenceRef);
      if (!presenceDoc.exists()) return null;
      const data = presenceDoc.data();
      return {
        userId: data.userId,
        status: data.status,
        lastSeen: data.lastSeen?.toDate() ?? new Date(),
        currentConversationId: data.currentConversationId,
        isTyping: data.isTyping ?? false,
        scope: data.scope,
        municipalityId: data.municipalityId,
      };
    } catch (error) {
      console.error('Failed to get user status:', error);
      return null;
    }
  }

  async setOnline(
    userId: string,
    scope: ChatScope,
    municipalityId?: string
  ): Promise<void> {
    return this.updateStatus(userId, 'online', scope, municipalityId);
  }

  async setOffline(userId: string): Promise<void> {
    try {
      const presenceRef = doc(db, 'user_presence', userId);
      await updateDoc(presenceRef, {
        status: 'offline',
        lastSeen: serverTimestamp(),
        isTyping: false,
        currentConversationId: null,
      });
    } catch (error) {
      console.error('Failed to set user offline:', error);
    }
  }

  async updateLastSeen(userId: string): Promise<void> {
    try {
      const presenceRef = doc(db, 'user_presence', userId);
      await updateDoc(presenceRef, { lastSeen: serverTimestamp() });
    } catch (error) {
      console.error('Failed to update last seen:', error);
    }
  }

  async setTypingInConversation(
    conversationId: string,
    userId: string,
    isTyping: boolean
  ): Promise<void> {
    try {
      const presenceRef = doc(db, 'user_presence', userId);
      await updateDoc(presenceRef, {
        isTyping,
        currentConversationId: isTyping ? conversationId : null,
        lastSeen: serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to set typing status:', error);
    }
  }

  onPresenceSnapshot(
    userIds: string[],
    callback: (presences: UserPresence[]) => void
  ): Unsubscribe[] {
    const unsubscribes: Unsubscribe[] = [];
    userIds.forEach((userId) => {
      const presenceRef = doc(db, 'user_presence', userId);
      const unsubscribe = onSnapshot(
        presenceRef,
        (snapshot: DocumentSnapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data();
            callback([{
              userId: data.userId,
              status: data.status,
              lastSeen: data.lastSeen?.toDate() ?? new Date(),
              currentConversationId: data.currentConversationId,
              isTyping: data.isTyping ?? false,
              scope: data.scope,
              municipalityId: data.municipalityId,
            }]);
          }
        },
        (error: Error) => {
          console.error(`Presence listener error for user ${userId}:`, error);
        }
      );
      unsubscribes.push(unsubscribe);
    });
    return unsubscribes;
  }

  initializePresence(
    userId: string,
    scope: ChatScope,
    municipalityId?: string
  ): () => void {
    this.setOnline(userId, scope, municipalityId);
    const heartbeatInterval = setInterval(() => {
      this.updateLastSeen(userId);
    }, 30000);
    const handleUnload = () => this.setOffline(userId);
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      clearInterval(heartbeatInterval);
      window.removeEventListener('beforeunload', handleUnload);
      this.setOffline(userId);
    };
  }
}

export const presenceService = new PresenceService();
